import React from "react";
import { Listing } from "../types";
import {
  MapPin,
  Bookmark,
  TrendingUp,
  User,
  ShoppingCart,
  Briefcase,
  Eye,
  Clock,
  XCircle,
  CheckCircle,
  Award,
  Coins,
  Heart,
  FileText,
  BarChart3,
  ShieldCheck,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { CATEGORIES } from "../constants";

interface BusinessCardProps {
  listing: Listing;
  layout?: "grid" | "list";
}

const BusinessCard: React.FC<BusinessCardProps> = ({
  listing,
  layout = "grid",
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    api.trackEvent("listing_click", listing.id, listing.title);
    navigate(`/listings/${listing.id}`);
  };

  const formatPrice = (price: number) => {
    if (price >= 1000000000) {
      return `${(price / 1000000000).toFixed(1).replace(/\.0$/, "")} میلیارد تومان`;
    }
    if (price >= 1000000) {
      return `${(price / 1000000).toFixed(0)} میلیون تومان`;
    }
    return `${price.toLocaleString()} تومان`;
  };

  const getStatusBadge = () => {
    switch (listing.status) {
      case "pending":
        return (
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-white shadow-lg shadow-amber-500/40 ring-2 ring-white/30 backdrop-blur-sm transition-transform hover:scale-105">
            <Clock size={14} className="animate-pulse" />
            <span className="text-xs font-bold">در انتظار بررسی</span>
          </div>
        );
      case "rejected":
        return (
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-600 text-white shadow-lg shadow-rose-600/40 ring-2 ring-white/30 backdrop-blur-sm transition-transform hover:scale-105">
            <XCircle size={14} />
            <span className="text-xs font-bold">رد شده</span>
          </div>
        );
      case "approved":
        // Optional: show a subtle approved badge if needed, mostly for dashboard views.
        // Currently keeping it hidden for public view cleanliness unless explicitly requested.
        return null;
      default:
        return null;
    }
  };

  const getTypeBadge = () => {
    switch (listing.type) {
      case "investment":
        return (
          <span className="bg-purple-100 text-purple-700 border border-purple-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <TrendingUp size={14} /> جذب سرمایه
          </span>
        );
      case "investor":
        return (
          <span className="bg-orange-100 text-orange-700 border border-orange-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <User size={14} /> سرمایه‌گذار
          </span>
        );
      case "demand":
        return (
          <span className="bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <ShoppingCart size={14} /> خریدار
          </span>
        );
      case "intangible":
        const assetLabel =
          listing.assetType === "brand"
            ? "برند"
            : listing.assetType === "software"
              ? "نرم‌افزار"
              : listing.assetType === "patent"
                ? "اختراع"
                : listing.assetType === "trademark"
                  ? "نام تجاری"
                  : "آگهی نامشهود";
        return (
          <span className="bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <Award size={14} /> {assetLabel}
          </span>
        );
      case "micro_investment":
        return (
          <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <Coins size={14} /> سرمایه‌گذاری خرد
          </span>
        );
      case "philanthropic":
        return (
          <span className="bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <Heart size={14} /> عام‌المنفعه
          </span>
        );
      default: // sale
        return (
          <span className="bg-teal-100 text-teal-700 border border-teal-200 text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 w-fit">
            <Briefcase size={14} /> فروش
          </span>
        );
    }
  };

  const isList = layout === "list";
  const hasAnyVerificationDoc = Boolean(
    listing.verificationDocsApproved &&
    listing.hasValuationFile &&
    listing.hasFinancialTurnoverFile &&
    listing.hasVerification3 &&
    listing.hasVerification4
  );
  const showVerificationBadges = listing.status === "approved";

  return (
    <div
      onClick={handleCardClick}
      className={`group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer relative ${isList ? "flex flex-col md:flex-row hover:-translate-y-1" : "flex flex-col h-full hover:-translate-y-2"}`}
    >
      <div
        className={`relative overflow-hidden ${isList ? "h-52 md:h-auto md:w-72 shrink-0" : "h-48 w-full"}`}
      >
        {getStatusBadge()}
        {showVerificationBadges && hasAnyVerificationDoc && (
          <div className="absolute top-3 left-3 z-10 bg-white/95 text-emerald-700 border border-emerald-200 text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm">
            دارای مدرک تاییدی
          </div>
        )}
        <img
          src={listing.imageUrls && listing.imageUrls.length > 0 ? listing.imageUrls[0] : 'https://tbiz.ir/nemone.png'}
          alt={listing.title}
          className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700 ease-out"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-60"></div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-base font-bold text-gray-900 mb-4 line-clamp-2 text-center min-h-[3rem]">
          {listing.title}
        </h3>

        {/* Verification Badges */}
        <div className="flex flex-wrap gap-1.5 mb-4 justify-center">
          {showVerificationBadges && listing.hasValuationFile && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100 text-[10px] font-bold" title="دارای فایل ارزش‌گذاری">
              <BarChart3 size={12} />
              <span>ارزش‌گذاری</span>
            </div>
          )}
          {showVerificationBadges && listing.hasFinancialTurnoverFile && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-green-50 text-green-700 border border-green-100 text-[10px] font-bold" title="دارای فایل گردش مالی">
              <FileText size={12} />
              <span>گردش مالی</span>
            </div>
          )}
          {showVerificationBadges && listing.hasVerification3 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-100 text-[10px] font-bold" title="تاییدیه حقوقی">
              <ShieldCheck size={12} />
              <span>تاییدیه حقوقی</span>
            </div>
          )}
          {showVerificationBadges && listing.hasVerification4 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-bold" title="تاییدیه فنی">
              <Check size={12} />
              <span>تاییدیه فنی</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500 mb-4 px-2">
          <span className="font-medium">
            {CATEGORIES.find((c) => c.id === listing.category)?.name ||
              listing.category}
          </span>
          <span className="font-bold text-gray-800">
            {formatPrice(listing.price)}
          </span>
        </div>

        <div className="mt-auto">
          <button className="w-full py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#152a45] transition-colors">
            مشاهده جزئیات
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessCard;
